import type { z } from 'zod';

/**
 * Transporte HTTP da aplicação: base URL, timeout, erros tipados e validação da resposta.
 *
 * Nenhum componente chama `fetch` direto. Toda chamada à API passa por `request` (ou
 * pelos atalhos `get`/`post`/`put`/`patch`/`del`), dentro de um serviço de domínio em
 * `services/` — é o que garante que toda falha chegue à tela como um destes erros, e que
 * o token de autenticação, quando existir, entre num lugar só.
 */

/** Timeout padrão: sem ele, uma requisição pendurada trava a tela para sempre. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Falha vinda do servidor (resposta com status fora do 2xx). */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown = null) {
    super(`${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

/** Falha antes de haver resposta: rede indisponível, DNS, CORS ou timeout. */
export class NetworkError extends Error {
  constructor(message: string, cause?: unknown) {
    // O erro original vai em `cause` (ES2022): preserva o `TypeError` do fetch ou o
    // `TimeoutError` do AbortSignal para quem for depurar.
    super(message, { cause });
    this.name = 'NetworkError';
  }
}

/**
 * A resposta chegou com status de sucesso, mas fora do formato esperado (o schema zod
 * passado em `request`). `cause` traz o erro de validação, com o campo que divergiu.
 */
export class ContractError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'ContractError';
  }
}

/** Qualquer falha de chamada à API — o tipo de erro padrão das queries (`queryClient.ts`). */
export type RequestError = ApiError | NetworkError | ContractError;

/**
 * Para onde vai a chamada à API.
 *
 * Em desenvolvimento, para a própria origem da página: o dev server do Vite repassa
 * `/api` para `VITE_API_URL` (ver `vite.config.ts`), e o navegador não esbarra em CORS.
 * No build, direto para `VITE_API_URL` — o deploy põe a API atrás da mesma origem ou
 * habilita o CORS para o domínio do front.
 */
export function resolveApiBaseUrl(
  env: { DEV: boolean; VITE_API_URL: string } = import.meta.env,
  origin: string = window.location.origin
): string {
  return env.DEV ? origin : env.VITE_API_URL.replace(/\/$/, '');
}

export type RequestOptions<TSchema extends z.ZodType = z.ZodType> = Omit<RequestInit, 'body'> & {
  /** Serializado como JSON automaticamente (a menos que já seja `FormData`/string). */
  body?: unknown;
  /** Query string a partir de um objeto; valores `undefined`/`null` são omitidos. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Valida a resposta; a divergência vira `ContractError`. */
  schema?: TSchema;
  timeoutMs?: number;
};

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(path, `${resolveApiBaseUrl()}/`);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';

  if (response.status === 204 || contentType === '') {
    return null;
  }

  if (contentType.includes('json')) {
    return response.json();
  }

  return response.text();
}

/**
 * Chamada à API. `path` é relativo à base (`/api/escalas`), e o retorno é tipado pelo
 * `schema` quando ele é passado — prefira sempre passar: o tipo sem validação é só uma
 * promessa do servidor.
 */
export async function request<TSchema extends z.ZodType>(
  path: string,
  options: RequestOptions<TSchema> & { schema: TSchema }
): Promise<z.infer<TSchema>>;
export async function request<TResponse = unknown>(
  path: string,
  options?: RequestOptions
): Promise<TResponse>;
export async function request(
  path: string,
  { body, query, schema, timeoutMs = DEFAULT_TIMEOUT_MS, headers, ...init }: RequestOptions = {}
): Promise<unknown> {
  const isPlainBody = body === undefined || body instanceof FormData || typeof body === 'string';
  const signal = init.signal
    ? AbortSignal.any([init.signal, AbortSignal.timeout(timeoutMs)])
    : AbortSignal.timeout(timeoutMs);

  let response: Response;

  try {
    // `globalThis.fetch` lido na chamada, e não guardado no import: é o que os testes trocam.
    response = await globalThis.fetch(buildUrl(path, query), {
      ...init,
      signal,
      headers: {
        Accept: 'application/json',
        ...(isPlainBody ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      body: isPlainBody ? (body as BodyInit | undefined) : JSON.stringify(body),
    });
  } catch (error) {
    // Cancelamento pedido por quem chamou (o React Query, ao desmontar a tela) segue
    // como `AbortError`, para não virar erro na tela.
    if (init.signal?.aborted) {
      throw error;
    }

    // `fetch` só rejeita antes de haver resposta — status de erro chega como resposta.
    throw new NetworkError('Não foi possível se comunicar com o servidor.', error);
  }

  const payload = await parseBody(response);

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, payload);
  }

  if (!schema) {
    return payload;
  }

  const parsed = schema.safeParse(payload);

  if (!parsed.success) {
    throw new ContractError('A resposta do servidor não está no formato esperado.', parsed.error);
  }

  return parsed.data;
}

type ShortcutOptions = Omit<RequestOptions, 'method'>;

export const get = <T = unknown>(path: string, options?: ShortcutOptions) =>
  request<T>(path, { ...options, method: 'GET' });
export const post = <T = unknown>(path: string, body?: unknown, options?: ShortcutOptions) =>
  request<T>(path, { ...options, method: 'POST', body });
export const put = <T = unknown>(path: string, body?: unknown, options?: ShortcutOptions) =>
  request<T>(path, { ...options, method: 'PUT', body });
export const patch = <T = unknown>(path: string, body?: unknown, options?: ShortcutOptions) =>
  request<T>(path, { ...options, method: 'PATCH', body });
export const del = <T = unknown>(path: string, options?: ShortcutOptions) =>
  request<T>(path, { ...options, method: 'DELETE' });
